import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// The app's left navigation. Top-level links come first; each top-level menu
// with a submenu (Superadmin, Admin, Utilities...) becomes a section under a
// small uppercase heading. Groups inside a section (Superadmin > Logs) stay
// collapsible, and open on their own when they hold the current page.

const rowBase = 'flex w-full items-center gap-2.5 min-h-[36px] px-2.5 py-1.5 rounded-lg text-sm text-left';
const rowIdle = 'text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink';
const rowActive = 'bg-ui-blue-bg text-ui-blue-fg font-semibold';

const ItemIcon = ({ icon: Icon }) => (Icon ? <Icon className="w-4 h-4 flex-none" aria-hidden="true" /> : null);

const tutorialAttr = item => (item.dataTutorial ? { 'data-tutorial': item.dataTutorial } : {});

// A link with a query ("/access-management?tab=teams") is active only with
// that query; a plain link is active on its path unless a ?tab picks another
// entry for the same page ("/admin?tab=settings" vs "/admin").
const hrefMatches = (href, location) => {
  if (!href) return false;
  const [path, query] = href.split('?');
  if (location.pathname !== path) return false;
  if (!query) return !new URLSearchParams(location.search).get('tab');
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(location.search);
  return [...want.entries()].every(([k, v]) => have.get(k) === v);
};

const containsActive = (item, location) => hrefMatches(item.href, location)
  || (item.submenu || []).some(child => containsActive(child, location));

const NavItem = ({ item, location, onNavigate }) => {
  if (item.onClick) {
    return (
      <button type="button" onClick={item.onClick} {...tutorialAttr(item)} className={`${rowBase} ${rowIdle}`}>
        <ItemIcon icon={item.icon} />{item.name}
      </button>
    );
  }
  const active = hrefMatches(item.href, location);
  return (
    <Link
      to={item.href}
      {...tutorialAttr(item)}
      aria-current={active ? 'page' : undefined}
      className={`${rowBase} ${active ? rowActive : rowIdle}`}
      onClick={onNavigate}
    >
      <ItemIcon icon={item.icon} />
      <span className="truncate">{item.name}</span>
    </Link>
  );
};

const NavGroup = ({ group, location, expandedMenus, toggleSubmenu, onNavigate }) => {
  const open = expandedMenus[group.name] ?? containsActive(group, location);
  const id = `nav-group-${group.name.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div>
      <button
        type="button"
        onClick={() => toggleSubmenu(group.name, !open)}
        aria-expanded={open}
        aria-controls={id}
        {...tutorialAttr(group)}
        className={`${rowBase} ${rowIdle} justify-between`}
      >
        <span className="flex items-center gap-2.5 min-w-0"><ItemIcon icon={group.icon} /><span className="truncate">{group.name}</span></span>
        <ChevronDown className={`w-4 h-4 flex-none text-ui-muted transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div id={id} className="ml-4 mt-0.5 pl-2 border-l border-ui-divider flex flex-col gap-0.5">
          {group.submenu.map(child => <NavItem key={child.name} item={child} location={location} onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
};

const AppSidebarNav = ({ navigation, expandedMenus = {}, toggleSubmenu, onNavigate }) => {
  const location = useLocation();
  const links = navigation.filter(item => !item.submenu);
  const sections = navigation.filter(item => item.submenu);

  return (
    <nav aria-label="Main" className="flex flex-col gap-0.5 px-3 py-3">
      {links.map(item => <NavItem key={item.name} item={item} location={location} onNavigate={onNavigate} />)}
      {sections.map(section => (
        <section key={section.name} aria-labelledby={`nav-section-${section.name.replace(/\W+/g, '-').toLowerCase()}`} className="flex flex-col gap-0.5">
          <h2
            id={`nav-section-${section.name.replace(/\W+/g, '-').toLowerCase()}`}
            {...tutorialAttr(section)}
            className="m-0 px-2.5 pt-4 pb-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase text-ui-muted"
          >
            {section.name}
          </h2>
          {section.submenu.map(child => (child.submenu ? (
            <NavGroup key={child.name} group={child} location={location} expandedMenus={expandedMenus} toggleSubmenu={toggleSubmenu} onNavigate={onNavigate} />
          ) : (
            <NavItem key={child.name} item={child} location={location} onNavigate={onNavigate} />
          )))}
        </section>
      ))}
    </nav>
  );
};

export default AppSidebarNav;
